import React, { useState, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { User, FileText, GraduationCap, Briefcase, Lightbulb, BookOpen, Award, Wrench, Users, Award as CertIcon, CheckCircle2, Edit2, Save, Download, Settings, QrCode, LayoutTemplate, FileOutput } from 'lucide-react';
import { toast } from 'sonner';
import { facultyData } from '../components/cv/facultyData';
import { CVPreviewModal } from '../components/cv/CVPreviewModal';
const SECTIONS = [{
  id: 'personal',
  label: 'Personal Info',
  icon: User,
  completed: true
}, {
  id: 'bio',
  label: 'Biography',
  icon: FileText,
  completed: true
}, {
  id: 'education',
  label: 'Education History',
  icon: GraduationCap,
  completed: true
}, {
  id: 'experience',
  label: 'Professional Experience',
  icon: Briefcase,
  completed: true
}, {
  id: 'research',
  label: 'Research Interests',
  icon: Lightbulb,
  completed: true
}, {
  id: 'publications',
  label: 'Publications',
  icon: BookOpen,
  completed: true
}, {
  id: 'projects',
  label: 'Research Projects',
  icon: FileText,
  completed: true
}, {
  id: 'awards',
  label: 'Awards & Achievements',
  icon: Award,
  completed: true
}, {
  id: 'skills',
  label: 'Skills',
  icon: Wrench,
  completed: true
}, {
  id: 'memberships',
  label: 'Professional Memberships',
  icon: Users,
  completed: true
}, {
  id: 'certifications',
  label: 'Trainings & Certs',
  icon: CertIcon,
  completed: true
}];
export function CVGeneratorPage() {
  const [activeSection, setActiveSection] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // Generation Settings
  const [selectedTemplate, setSelectedTemplate] = useState<'academic' | 'research' | 'standard'>('academic');
  const [autoUpdatePubs, setAutoUpdatePubs] = useState(true);
  const [showQR, setShowQR] = useState(true);
  const [showPageNumbers, setShowPageNumbers] = useState(true);
  const handleSave = () => {
    setIsEditing(false);
    toast.success('Section updated successfully');
  };
  const renderEditorContent = () => {
    switch (activeSection) {
      case 'personal':
        return <div className="space-y-6" data-id="element-713">
            <div className="flex items-center space-x-6" data-id="element-714">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-slate-100 shrink-0" data-id="element-715">
                <img src={facultyData.personalInfo.profilePhoto} alt="Profile" className="w-full h-full object-cover" data-id="element-716" />
              </div>
              {isEditing && <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors" data-id="element-717">
                  Change Photo
                </button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-718">
              <div data-id="element-719">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-720">
                  Full Name
                </label>
                {isEditing ? <input type="text" defaultValue={facultyData.personalInfo.fullName} className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none" data-id="element-721" /> : <div className="p-2 bg-slate-50 rounded-lg border border-transparent font-medium" data-id="element-722">
                    {facultyData.personalInfo.fullName}
                  </div>}
              </div>
              <div data-id="element-723">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-724">
                  Designation
                </label>
                {isEditing ? <input type="text" defaultValue={facultyData.personalInfo.designation} className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none" data-id="element-725" /> : <div className="p-2 bg-slate-50 rounded-lg border border-transparent font-medium" data-id="element-726">
                    {facultyData.personalInfo.designation}
                  </div>}
              </div>
              <div className="md:col-span-2" data-id="element-727">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-728">
                  Department
                </label>
                {isEditing ? <input type="text" defaultValue={facultyData.personalInfo.department} className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none" data-id="element-729" /> : <div className="p-2 bg-slate-50 rounded-lg border border-transparent font-medium" data-id="element-730">
                    {facultyData.personalInfo.department}
                  </div>}
              </div>
              <div data-id="element-731">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-732">
                  Email
                </label>
                {isEditing ? <input type="email" defaultValue={facultyData.personalInfo.email} className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none" data-id="element-733" /> : <div className="p-2 bg-slate-50 rounded-lg border border-transparent font-medium" data-id="element-734">
                    {facultyData.personalInfo.email}
                  </div>}
              </div>
              <div data-id="element-735">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-736">
                  Phone
                </label>
                {isEditing ? <input type="text" defaultValue={facultyData.personalInfo.phone} className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-navy outline-none" data-id="element-737" /> : <div className="p-2 bg-slate-50 rounded-lg border border-transparent font-medium" data-id="element-738">
                    {facultyData.personalInfo.phone}
                  </div>}
              </div>
            </div>
          </div>;
      case 'bio':
        return <div data-id="element-739">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2" data-id="element-740">
              Professional Biography
            </label>
            {isEditing ? <textarea defaultValue={facultyData.biography} rows={8} className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy focus:border-navy outline-none resize-none" data-id="element-741" /> : <div className="p-4 bg-slate-50 rounded-xl border border-transparent text-slate-700 leading-relaxed" data-id="element-742">
                {facultyData.biography}
              </div>}
          </div>;
      case 'education':
        return <div className="space-y-4" data-id="element-743">
            {facultyData.education.map((edu, i) => <div key={i} className="p-4 border border-slate-200 rounded-xl bg-white relative group" data-id="element-744">
                {isEditing && <button className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition-colors" data-id="element-745">
                    <X className="w-4 h-4" data-id="element-746" />
                  </button>}
                <div className="flex justify-between items-start mb-2" data-id="element-747">
                  <div className="font-bold text-navy" data-id="element-748">{edu.degree}</div>
                  <div className="text-sm font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded" data-id="element-749">
                    {edu.year}
                  </div>
                </div>
                <div className="text-slate-700 font-medium mb-1" data-id="element-750">
                  {edu.institution}
                </div>
                {edu.details && <div className="text-sm text-slate-500" data-id="element-751">{edu.details}</div>}
              </div>)}
            {isEditing && <button className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 font-medium hover:border-navy hover:text-navy transition-colors" data-id="element-752">
                + Add Education
              </button>}
          </div>;
      case 'publications':
        return <div className="space-y-6" data-id="element-753">
            <div className="flex items-center justify-between bg-teal/10 p-4 rounded-xl border border-teal/20" data-id="element-754">
              <div data-id="element-755">
                <div className="font-bold text-teal flex items-center" data-id="element-756">
                  <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-757" /> Google Scholar Sync
                  Active
                </div>
                <div className="text-xs text-teal/70 mt-1" data-id="element-758">
                  Last synced: Today at 09:41 AM
                </div>
              </div>
              <button className="px-4 py-2 bg-white text-teal text-sm font-bold rounded-lg shadow-sm border border-teal/20 hover:bg-teal hover:text-white transition-colors" data-id="element-759">
                Sync Now
              </button>
            </div>

            <div className="flex space-x-2 mb-4" data-id="element-760">
              {['All', 'Journal', 'Conference', 'Book'].map(filter => <button key={filter} className="px-3 py-1.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-761">
                  {filter}
                </button>)}
            </div>

            <div className="space-y-3" data-id="element-762">
              {facultyData.publications.map((pub, i) => <div key={i} className="p-4 border border-slate-200 rounded-xl bg-white hover:border-slate-300 transition-colors" data-id="element-763">
                  <div className="flex justify-between items-start gap-4" data-id="element-764">
                    <div data-id="element-765">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-766">
                        {pub.type}
                      </div>
                      <div className="font-medium text-slate-900 mb-1" data-id="element-767">
                        {pub.title}
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-768">
                        {pub.authors} • {pub.venue} ({pub.year})
                      </div>
                    </div>
                    <div className="shrink-0 text-center bg-slate-50 px-3 py-2 rounded-lg border border-slate-100" data-id="element-769">
                      <div className="text-lg font-bold text-navy" data-id="element-770">
                        {pub.citations}
                      </div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-771">
                        Citations
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>;
      // Fallback for other sections to keep it concise
      default:
        return <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-id="element-772">
            <Settings className="w-12 h-12 mb-4 opacity-20" data-id="element-773" />
            <p data-id="element-774">
              Editor for {SECTIONS.find(s => s.id === activeSection)?.label}{' '}
              is ready to be implemented.
            </p>
          </div>;
    }
  };
  return <div className="min-h-screen bg-slate-50 pt-24 pb-12" data-id="element-775">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6" data-id="element-776">
        {/* Page Header */}
        <div className="mb-8" data-id="element-777">
          <h1 className="text-3xl font-bold text-navy mb-2" data-id="element-778">
            Faculty CV Generator
          </h1>
          <p className="text-slate-600" data-id="element-779">
            Manage your professional profile and generate branded academic CVs.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start" data-id="element-780">
          {/* LEFT RAIL: Navigation & Progress */}
          <div className="w-full lg:w-[280px] shrink-0 space-y-6 lg:sticky lg:top-28" data-id="element-781">
            {/* Profile Mini Card */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center space-x-4" data-id="element-782">
              <img src={facultyData.personalInfo.profilePhoto} alt="Profile" className="w-12 h-12 rounded-full object-cover border-2 border-slate-100" data-id="element-783" />
              <div data-id="element-784">
                <div className="font-bold text-slate-900 text-sm" data-id="element-785">
                  {facultyData.personalInfo.fullName}
                </div>
                <div className="text-xs text-slate-500 truncate max-w-[150px]" data-id="element-786">
                  {facultyData.personalInfo.department}
                </div>
              </div>
            </div>

            {/* Completion Meter */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm" data-id="element-787">
              <div className="flex justify-between items-end mb-2" data-id="element-788">
                <div className="text-sm font-bold text-slate-700" data-id="element-789">
                  Profile Completion
                </div>
                <div className="text-lg font-bold text-teal" data-id="element-790">92%</div>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-791">
                <div className="h-full bg-teal rounded-full" style={{
                width: '92%'
              }} data-id="element-792" />
              </div>
            </div>

            {/* Section Nav */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-793">
              <div className="p-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-794">
                Profile Sections
              </div>
              <div className="p-2 space-y-1" data-id="element-795">
                {SECTIONS.map(section => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return <button key={section.id} onClick={() => {
                  setActiveSection(section.id);
                  setIsEditing(false);
                }} className={`w-full flex items-center justify-between p-3 rounded-xl text-sm transition-colors ${isActive ? 'bg-navy text-white font-medium shadow-md' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-796">
                      <div className="flex items-center" data-id="element-797">
                        <Icon className={`w-4 h-4 mr-3 ${isActive ? 'text-white/80' : 'text-slate-400'}`} data-id="element-798" />
                        {section.label}
                      </div>
                      {section.completed && <CheckCircle2 className={`w-4 h-4 ${isActive ? 'text-white' : 'text-teal'}`} data-id="element-799" />}
                    </button>;
              })}
              </div>
            </div>
          </div>

          {/* CENTER: Editor Surface */}
          <div className="flex-grow w-full min-w-0" data-id="element-800">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden" data-id="element-801">
              {/* Editor Header */}
              <div className="px-8 py-6 border-b border-slate-200 flex justify-between items-center bg-slate-50/50" data-id="element-802">
                <div className="flex items-center" data-id="element-803">
                  <div className="w-10 h-10 rounded-xl bg-navy/5 flex items-center justify-center mr-4" data-id="element-804">
                    {createElement(SECTIONS.find(s => s.id === activeSection)?.icon || Settings, {
                    className: 'w-5 h-5 text-navy'
                  })}
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-805">
                    {SECTIONS.find(s => s.id === activeSection)?.label}
                  </h2>
                </div>

                {isEditing ? <div className="flex space-x-2" data-id="element-806">
                    <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-807">
                      Cancel
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 bg-navy text-white text-sm font-bold rounded-lg hover:bg-navy-deep transition-colors flex items-center shadow-sm" data-id="element-808">
                      <Save className="w-4 h-4 mr-2" data-id="element-809" /> Save Changes
                    </button>
                  </div> : <button onClick={() => setIsEditing(true)} className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center" data-id="element-810">
                    <Edit2 className="w-4 h-4 mr-2" data-id="element-811" /> Edit Section
                  </button>}
              </div>

              {/* Editor Body */}
              <div className="p-8 min-h-[500px]" data-id="element-812">{renderEditorContent()}</div>
            </div>
          </div>

          {/* RIGHT RAIL: CV Generation Panel */}
          <div className="w-full lg:w-[320px] shrink-0 space-y-6 lg:sticky lg:top-28" data-id="element-813">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden" data-id="element-814">
              <div className="bg-navy p-6 text-center" data-id="element-815">
                <FileOutput className="w-8 h-8 text-gold mx-auto mb-3" data-id="element-816" />
                <h3 className="text-lg font-bold text-white mb-1" data-id="element-817">
                  Ready to Generate?
                </h3>
                <p className="text-white/70 text-sm" data-id="element-818">
                  Your profile is 92% complete
                </p>
              </div>

              <div className="p-6 space-y-6" data-id="element-819">
                {/* Template Picker */}
                <div data-id="element-820">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center" data-id="element-821">
                    <LayoutTemplate className="w-4 h-4 mr-2" data-id="element-822" /> Select Template
                  </label>
                  <div className="space-y-3" data-id="element-823">
                    {[{
                    id: 'academic',
                    name: 'Academic',
                    desc: 'Traditional 2-column layout'
                  }, {
                    id: 'research',
                    name: 'Research',
                    desc: 'Focus on publications & grants'
                  }, {
                    id: 'standard',
                    name: 'Standard',
                    desc: 'Clean modern single column'
                  }].map(tpl => <button key={tpl.id} onClick={() => setSelectedTemplate(tpl.id as any)} className={`w-full flex items-center p-3 rounded-xl border-2 text-left transition-all ${selectedTemplate === tpl.id ? 'border-navy bg-navy/5 shadow-sm' : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'}`} data-id="element-824">
                        <div className="w-10 h-12 bg-white border border-slate-200 rounded shadow-sm mr-3 flex flex-col p-1 gap-0.5 shrink-0" data-id="element-825">
                          {/* Tiny wireframe representation */}
                          <div className="w-full h-1.5 bg-slate-200 rounded-sm" data-id="element-826" />
                          <div className="flex gap-1 mt-1" data-id="element-827">
                            {tpl.id === 'academic' && <div className="w-1/3 h-6 bg-slate-100 rounded-sm" data-id="element-828" />}
                            <div className="flex-1 h-6 bg-slate-100 rounded-sm" data-id="element-829" />
                          </div>
                        </div>
                        <div data-id="element-830">
                          <div className={`font-bold text-sm ${selectedTemplate === tpl.id ? 'text-navy' : 'text-slate-700'}`} data-id="element-831">
                            {tpl.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-832">
                            {tpl.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-4 pt-4 border-t border-slate-100" data-id="element-833">
                  <label className="flex items-center justify-between cursor-pointer group" data-id="element-834">
                    <span className="text-sm font-medium text-slate-700 group-hover:text-navy transition-colors" data-id="element-835">
                      Auto-update publications
                    </span>
                    <div className={`w-10 h-5 rounded-full transition-colors relative ${autoUpdatePubs ? 'bg-teal' : 'bg-slate-200'}`} onClick={() => setAutoUpdatePubs(!autoUpdatePubs)} data-id="element-836">
                      <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${autoUpdatePubs ? 'left-6' : 'left-1'}`} data-id="element-837" />
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer group" data-id="element-838">
                    <span className="text-sm font-medium text-slate-700 group-hover:text-navy transition-colors" data-id="element-839">
                      Include QR Code
                    </span>
                    <div className={`w-10 h-5 rounded-full transition-colors relative ${showQR ? 'bg-teal' : 'bg-slate-200'}`} onClick={() => setShowQR(!showQR)} data-id="element-840">
                      <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${showQR ? 'left-6' : 'left-1'}`} data-id="element-841" />
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer group" data-id="element-842">
                    <span className="text-sm font-medium text-slate-700 group-hover:text-navy transition-colors" data-id="element-843">
                      Page Numbers
                    </span>
                    <div className={`w-10 h-5 rounded-full transition-colors relative ${showPageNumbers ? 'bg-teal' : 'bg-slate-200'}`} onClick={() => setShowPageNumbers(!showPageNumbers)} data-id="element-844">
                      <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${showPageNumbers ? 'left-6' : 'left-1'}`} data-id="element-845" />
                    </div>
                  </label>
                </div>

                {/* QR Preview */}
                {showQR && <div className="pt-4 border-t border-slate-100 flex items-center justify-center" data-id="element-846">
                    <div className="text-center" data-id="element-847">
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(facultyData.personalInfo.profileUrl)}&color=1C4370&bgcolor=ffffff`} alt="QR Preview" className="w-16 h-16 mx-auto mb-2 border border-slate-200 p-1 rounded-lg" data-id="element-848" />
                      <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider" data-id="element-849">
                        Profile Link
                      </div>
                    </div>
                  </div>}

                {/* Actions */}
                <div className="pt-2 space-y-3" data-id="element-850">
                  <button onClick={() => setIsPreviewOpen(true)} className="w-full bg-gold hover:bg-gold-hover text-white font-bold py-3.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center justify-center" data-id="element-851">
                    <FileText className="w-5 h-5 mr-2" data-id="element-852" /> Generate CV
                  </button>
                  <button onClick={() => {
                  toast.success('PDF download started');
                }} className="w-full bg-white border-2 border-slate-200 hover:border-navy text-slate-700 hover:text-navy font-bold py-3 px-4 rounded-xl transition-colors flex items-center justify-center" data-id="element-853">
                    <Download className="w-4 h-4 mr-2" data-id="element-854" /> Quick Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CVPreviewModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} template={selectedTemplate} showQR={showQR} showPageNumbers={showPageNumbers} data-id="element-855" />
    </div>;
}